import * as THREE from 'three';
import { lightningEffects, type LightningLook } from '../../../content/spells/effects.js';
import type { SkillEffect, EffectDimensions } from './skill-effects.js';
import { effectMaterial, effectParticles, pathRibbon, shadeEffect } from './effect-surfaces.js';

/** Abrupt discharge and two weaker restrikes, followed by a short electrical afterglow. */
const discharge = (p: number) => Math.min(1, p * 70) * Math.max(0, 1 - p) *
  (Math.exp(-p * 7) + .8 * Math.exp(-(((p - .23) / .055) ** 2)) + .55 * Math.exp(-(((p - .48) / .075) ** 2)) + .12);

/** White-hot contact nucleus, outward electrical splinters and expanding pressure rings. */
export function lightningImpact(height: number, radius: number, tuning: LightningLook = lightningEffects): SkillEffect {
  const group = new THREE.Group(); group.name = 'lightning-contact-burst';
  const core = new THREE.Mesh(new THREE.SphereGeometry(.15, 24, 16), effectMaterial(0xb6dfff, 0xffffff, 4, .035));
  const corona = new THREE.Mesh(new THREE.SphereGeometry(.34, 32, 20), effectMaterial(0x639fff, 0xe8f3ff, 4, .055));
  corona.userData.baseOpacity = .45; group.add(core, corona);
  const rings = Array.from({ length: 2 }, (_, i) => {
    const ring = new THREE.Mesh(new THREE.TorusGeometry(.38, .018, 6, 64), effectMaterial(0x87bbff, 0xffffff, 4));
    ring.rotation.x = Math.PI / 2 + i * .8; ring.userData.baseOpacity = .45; group.add(ring); return ring;
  });
  const splinters = Array.from({ length: 8 }, (_, i) => {
    const path = pathRibbon(0x98c9ff, .022, 4); path.mesh.userData.baseOpacity = .75; group.add(path.mesh);
    return { path, start: new THREE.Vector3(), end: new THREE.Vector3(), angle: i * Math.PI / 4 };
  });
  group.add(effectParticles(0xc7e5ff, tuning.sparks, Math.max(.7, radius * 1.3) / tuning.size, .45, 1));
  const light = { position: new THREE.Vector3(), color: new THREE.Color(0x91bdff), output: 0 };
  return { group, duration: tuning.impactDuration * 1000, lights: [light], update(p, _from, to, time = p * tuning.impactDuration, ppm = 40) {
    group.position.copy(to).y += height * .55; group.scale.setScalar(tuning.size);
    const energy = discharge(p);
    core.scale.setScalar(.75 + Math.exp(-p * 7) * .9); corona.scale.setScalar(.65 + p * 2.1);
    rings.forEach((ring, i) => { ring.scale.setScalar(.45 + p * (2.5 + i * .8)); ring.rotation.z = time * (i ? -3 : 3); });
    splinters.forEach(({ path, start, end, angle }, i) => {
      const length = (.55 + p * .65) * (1 + i % 3 * .15);
      start.set(Math.cos(angle) * .08, 0, Math.sin(angle) * .08);
      end.set(Math.cos(angle) * length, Math.sin(angle * 2 + .6) * .48, Math.sin(angle) * length);
      path.update(start, end, time, true, i * .7, 1.5);
    });
    shadeEffect(group, time, energy, Math.min(1, p * 4), ppm);
    light.position.copy(group.position); light.output = energy * tuning.impactLight;
  } };
}

/**
 * One heavy, sharply kinked conductor with a matching corona and subordinate offshoots. A white spine is drawn with
 * ordinary blending inside the additive glow, so the bolt stays solid over bright ground instead of washing out.
 */
export function lightningDischarge(dimensions: EffectDimensions, tuning: LightningLook = lightningEffects): SkillEffect {
  const group = new THREE.Group(); group.name = 'skill-arc';
  const knots = 14;
  const conductors = [pathRibbon(0xb0dbff, tuning.coreWidth, 4, 0xffffff, knots),
    pathRibbon(0x598fff, tuning.coronaWidth, 4, 0xe2efff, knots), pathRibbon(0xeef7ff, tuning.coreWidth * .45, 4, 0xffffff, knots)];
  conductors.forEach(path => group.add(path.mesh));
  conductors[0].mesh.name = 'lightning-central-ray'; conductors[1].mesh.name = 'lightning-central-corona'; conductors[2].mesh.name = 'lightning-spine';
  conductors[1].mesh.userData.baseOpacity = .38;
  (conductors[2].mesh.material as THREE.ShaderMaterial).blending = THREE.NormalBlending; conductors[2].mesh.renderOrder = 1;
  const branches = Array.from({ length: tuning.branches }, (_, i) => {
    const path = pathRibbon(0xadd4ff, tuning.coreWidth * .22, 4, 0xe8f4ff, 8); path.mesh.userData.baseOpacity = .65; group.add(path.mesh);
    return { path, start: new THREE.Vector3(), end: new THREE.Vector3(), fraction: (i + 1) / (tuning.branches + 1), sign: i % 2 ? -1 : 1 };
  });
  const launch = new THREE.Mesh(new THREE.SphereGeometry(.19 * tuning.size, 24, 16), effectMaterial(0x92c6ff, 0xffffff, 4, .045)); group.add(launch);
  const impact = lightningImpact(dimensions.targetHeight, dimensions.targetRadius, tuning); group.add(impact.group);
  const fromAt = new THREE.Vector3(), toAt = new THREE.Vector3(), zero = new THREE.Vector3();
  const launchLight = { position: new THREE.Vector3(), color: new THREE.Color(0xa2caff), output: 0 };
  return { group, duration: tuning.duration * 1000, lights: [launchLight, ...impact.lights!], update(p, from, to, time = p * tuning.duration, ppm = 40) {
    fromAt.copy(from); if (!dimensions.launchAtOrb) fromAt.y += dimensions.actorHeight * .62;
    toAt.copy(to).y += dimensions.targetHeight * .55;
    const energy = discharge(p), front = Math.min(1, p / .16);
    conductors.forEach(path => path.update(fromAt, toAt, time, true, 0, tuning.jaggedness));
    const dx = toAt.x - fromAt.x, dz = toAt.z - fromAt.z, distance = Math.hypot(dx, dz) || 1;
    branches.forEach(({ path, start, end, fraction, sign }, i) => {
      const t = Math.round(fraction * knots) / knots, index = Math.round(t * knots), spread = Math.sin(t * Math.PI);
      const jitter = Math.sin(index * 12.989 + Math.floor(time * 28) * 4.71) * .12 * tuning.jaggedness * spread;
      start.copy(fromAt).lerp(toAt, t); start.x -= dz / distance * jitter; start.z += dx / distance * jitter;
      start.y += Math.cos(index * 7.7 + Math.floor(time * 28)) * .08 * tuning.jaggedness * spread;
      end.copy(fromAt).lerp(toAt, Math.min(1, t + .1));
      end.x -= dz / distance * sign * (.16 + spread * .18); end.z += dx / distance * sign * (.16 + spread * .18);
      end.y += sign * (.08 + spread * .13); path.update(start, end, time, true, i, 1.2);
    });
    launch.position.copy(fromAt); launch.scale.setScalar(.7 + energy * 1.2);
    shadeEffect(group, time, energy, front, ppm);
    const contact = Math.max(0, (p - .08) / .84); impact.update(contact, zero, to, Math.max(0, time - .08 * tuning.duration), ppm);
    impact.group.visible = p >= .08;
    launchLight.position.copy(fromAt); launchLight.output = energy * tuning.launchLight;
    if (p < .08) impact.lights![0].output = 0;
  } };
}
